import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from './components/Navbar';
import { DateRangeFilter } from './components/DateRangeFilter';
import { ChemicalSelector } from './components/ChemicalSelector';
import { SummaryCards } from './components/SummaryCards';
import { ReportTable } from './components/ReportTable';
import { SignatoryModal } from './components/SignatoryModal';
import { ChemicalType, ReportApiResponse, Signatory } from './types';
import { fetchReportData } from './services/api';
import { AlertCircle, Loader2 } from 'lucide-react';
import { format, subDays } from 'date-fns';

const DEFAULT_SIGNATORIES: Signatory[] = [
  { id: 'sig_1', roleTitle: 'ผู้จัดทำ', name: '', position: '' },
  { id: 'sig_2', roleTitle: 'ผู้รายงาน', name: '', position: '' },
  { id: 'sig_3', roleTitle: 'ผู้ตรวจสอบ', name: '', position: '' },
  { id: 'sig_4', roleTitle: 'ผู้อนุมัติ', name: '', position: '' },
];

export const App: React.FC = () => {
  // Default to 7 days ending on yesterday (เมื่อวาน)
  const yesterday = subDays(new Date(), 1);
  const defaultEnd = format(yesterday, 'yyyy-MM-dd');
  const defaultStart = format(subDays(yesterday, 6), 'yyyy-MM-dd'); // 7 days inclusive

  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);
  const [selectedChemical, setSelectedChemical] = useState<ChemicalType | 'all'>('alum');

  // Signatories state (loaded from localStorage)
  const [signatories, setSignatories] = useState<Signatory[]>(() => {
    try {
      const saved = localStorage.getItem('mhchem_signatories');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length >= 4) {
          return parsed;
        } else if (Array.isArray(parsed) && parsed.length === 3) {
          // Migrate old 3 signatories format to 4
          return DEFAULT_SIGNATORIES.map((def, idx) => ({
            ...def,
            name: parsed[idx]?.name || '',
            position: parsed[idx]?.position || '',
            roleTitle: (parsed[idx]?.roleTitle && parsed[idx]?.roleTitle !== 'ผู้จัดทำ / ผู้รายงาน')
              ? parsed[idx].roleTitle
              : def.roleTitle,
          }));
        }
      }
    } catch (e) {
      console.warn('Could not load signatories from localStorage', e);
    }
    return DEFAULT_SIGNATORIES;
  });
  const [isSignatoryModalOpen, setIsSignatoryModalOpen] = useState<boolean>(false);

  const handleSaveSignatories = (newSignatories: Signatory[]) => {
    setSignatories(newSignatories);
    try {
      localStorage.setItem('mhchem_signatories', JSON.stringify(newSignatories));
    } catch (e) {
      console.error('Could not save signatories to localStorage', e);
    }
  };


  const [data, setData] = useState<ReportApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (start: string, end: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchReportData(start, end);
      setData(result);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลจาก BigQuery');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData(startDate, endDate);
  }, []);

  const handleDateChange = (newStart: string, newEnd: string) => {
    setStartDate(newStart);
    setEndDate(newEnd);
    loadData(newStart, newEnd);
  };

  const handleRefresh = () => {
    loadData(startDate, endDate);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Navbar onOpenSignatoryModal={() => setIsSignatoryModalOpen(true)} />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Header Title Section */}
        <div className="border-b border-slate-200/80 pb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
            รายงานปริมาณการรับ-ใช้ และสต็อกสารเคมี
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            ดึงข้อมูลตรงจาก BigQuery โรงงานผลิตน้ำมหาสวัสดิ์
          </p>
        </div>

        {/* Date Filter */}
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          onRefresh={handleRefresh}
          isLoading={isLoading}
        />

        {/* Chemical Selector Buttons */}
        <ChemicalSelector
          selectedChemical={selectedChemical}
          onSelect={setSelectedChemical}
        />

        {/* Error Alert */}
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-sm">ไม่สามารถโหลดข้อมูลได้</h4>
              <p className="text-xs text-red-700 mt-1">{error}</p>
              <button
                type="button"
                onClick={handleRefresh}
                className="mt-2 text-xs font-semibold underline hover:text-red-950"
              >
                ลองใหม่อีกครั้ง
              </button>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {isLoading && !data && (
          <div className="py-16 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
            <span className="text-sm font-medium text-slate-600">กำลังดึงข้อมูลจาก Google BigQuery...</span>
            <span className="text-xs text-slate-400 mt-1">โปรดรอสักครู่</span>
          </div>
        )}

        {/* Empty State when no data in BigQuery */}
        {data && data.rows.length === 0 && !isLoading && (
          <div className="bg-white rounded-xl border border-amber-200 bg-amber-50/40 p-6 sm:p-8 text-center shadow-xs">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3 text-amber-600">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-base font-bold text-slate-800">ยังไม่มีข้อมูล</h3>
            <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-md mx-auto">
              วันที่เลือก ({startDate} ถึง {endDate}) ยังไม่มีข้อมูลบน Google BigQuery
            </p>
          </div>
        )}

        {/* Summary Cards */}
        {data && data.rows.length > 0 && (
          <SummaryCards
            chemical={selectedChemical}
            rows={data.rows}
            previousDayStock={data.previousDayStock}
          />
        )}

        {/* Data Table */}
        {data && (
          <ReportTable
            chemical={selectedChemical}
            rows={data.rows}
            previousDayStock={data.previousDayStock}
            startDate={startDate}
            endDate={endDate}
            signatories={signatories}
            onOpenSignatorySettings={() => setIsSignatoryModalOpen(true)}
          />
        )}
      </main>

      {/* Signatories Configuration Modal */}
      <SignatoryModal
        isOpen={isSignatoryModalOpen}
        onClose={() => setIsSignatoryModalOpen(false)}
        signatories={signatories}
        onSave={handleSaveSignatories}
      />

      <footer className="mt-auto border-t border-slate-200 bg-white py-4 text-center text-xs text-slate-500">
        <div className="flex items-center justify-center gap-2 mb-1">
          <img src="/mwa_logo.png" alt="MWA Logo" className="w-5 h-5 object-contain" />
          <p className="font-medium text-slate-600">ระบบรายงานข้อมูลสารเคมี • กองผลิตและจ่ายน้ำ ฝ่ายโรงงานผลิตน้ำมหาสวัสดิ์ การประปานครหลวง (MWA)</p>
        </div>
        <p className="text-[11px] text-slate-400">
          เชื่อมต่อ Google BigQuery REST API & Cloudflare Pages Functions
        </p>
      </footer>
    </div>
  );
};

export default App;
